import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { 
  Users, 
  GraduationCap, 
  Calendar, 
  DollarSign, 
  Settings, 
  Menu,
  Home,
  LogOut,
  School,
  BookOpen
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const navigationItems = [
    { name: "Dashboard", icon: Home, path: "/admin/dashboard" },
    { name: "Students", icon: Users, path: "/admin/students" },
    { name: "Teachers", icon: GraduationCap, path: "/admin/teachers" },
    { name: "Classes", icon: BookOpen, path: "/admin/classes" },
    { name: "Attendance", icon: Calendar, path: "/admin/attendance" },
    { name: "Fee Management", icon: DollarSign, path: "/admin/fees" },
    { name: "Settings", icon: Settings, path: "/admin/settings" },
  ];

  const handleLogout = () => {
    localStorage.removeItem('adminData');
    toast({
      title: "Logged out successfully",
      description: "See you soon!"
    });
    navigate('/');
  };

  const NavigationContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <School className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-800">TattvaSphere</h2>
            <p className="text-sm text-gray-600">Admin Portal</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {navigationItems.map((item) => (
            <li key={item.path}>
              <Button
                variant={location.pathname === item.path ? "default" : "ghost"}
                className={`w-full justify-start ${
                  location.pathname === item.path 
                    ? "bg-red-600 text-white" 
                    : "text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => {
                  navigate(item.path);
                  setIsOpen(false);
                }}
              >
                <item.icon className="w-4 h-4 mr-3" />
                {item.name}
              </Button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="p-4 border-t">
        <Button 
          variant="outline" 
          className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50"
          onClick={handleLogout}
        >
          <LogOut className="w-4 h-4 mr-3" />
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="lg:hidden bg-white shadow-sm p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
            <School className="w-4 h-4 text-red-600" />
          </div>
          <h1 className="font-semibold text-gray-800">TattvaSphere Admin</h1>
        </div>
        
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon">
              <Menu className="w-4 h-4" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64">
            <NavigationContent />
          </SheetContent>
        </Sheet>
      </div>

      <div className="lg:flex">
        <div className="hidden lg:block w-64 bg-white shadow-sm h-screen fixed">
          <NavigationContent />
        </div>

        <main className="lg:ml-64 flex-1">
          <div className="p-4 lg:p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
